/**
 * The Übersicht hero's derived facts (COL-533).
 *
 * The design opens with a date line, a greeting, a one-line summary of where
 * the tenant is, and a room photo carrying a stay label and progress bar —
 * then a five-step journey strip underneath, until the tenancy is live.
 *
 * None of that needs new data: every value here comes off the booking's own
 * dates and the journey stage we already resolve. Pure, so the month and
 * boundary arithmetic can be tested without rendering anything.
 */

import { parseDateOnly } from "@/lib/roomAvailability";
import {
  TENANT_JOURNEY_STAGES,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";

/**
 * The design's five steps, and which journey stage lands on each.
 *
 * Ours has ten stages; the design has five. `key_pickup_pending` and
 * `handover_pending` are both "Move-in" to a tenant — the distinction between
 * having the key and having filed the protocol is ours, not theirs — and
 * everything from `active` onward is "Moved in".
 */
export const HERO_JOURNEY_STEPS = [
  "application",
  "contract",
  "deposit",
  "moveIn",
  "movedIn",
] as const;

export type HeroJourneyStep = (typeof HERO_JOURNEY_STEPS)[number];

const STAGE_TO_STEP: Partial<Record<TenantJourneyStageId, number>> = {
  applied: 0,
  signature_pending: 1,
  deposit_pending: 2,
  key_pickup_pending: 3,
  handover_pending: 3,
};

/**
 * 0-based index into HERO_JOURNEY_STEPS.
 *
 * Anything at or past `active` — including the move-out stages — is step 4:
 * a tenant giving notice has still moved in, and the strip is a record of how
 * they got here, not a countdown to leaving.
 */
export function heroJourneyIndex(
  stage: TenantJourneyStageId | null | undefined
): number {
  if (!stage) return 0;
  const mapped = STAGE_TO_STEP[stage];
  return mapped ?? 4;
}

/**
 * Whether the strip is shown at all.
 *
 * The design drops it once the tenancy is live (`showJourney` is `p <= 3`) —
 * at that point the three Wohnen cards take its place, and a five-step strip
 * reading "all done" is just noise above them.
 */
export function showHeroJourney(
  stage: TenantJourneyStageId | null | undefined
): boolean {
  return heroJourneyIndex(stage) < 4;
}

export type StayProgress =
  | { kind: "before_move_in"; moveInIso: string | null; percent: null }
  | { kind: "days_until_move_in"; days: number; percent: null }
  | { kind: "living"; day: number; total: number; percent: number }
  | { kind: "ending"; daysLeft: number; percent: number }
  | { kind: "unknown"; percent: null };

/** Inside this many days of move-in, the design counts down instead of dating. */
export const MOVE_IN_COUNTDOWN_DAYS = 7;
/** Inside this many days of the end, the design counts down what is left. */
export const MOVE_OUT_COUNTDOWN_DAYS = 30;

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const DAY_MS = 86_400_000;

/**
 * Where the tenant is in their stay.
 *
 * `parseDateOnly`, not `new Date`: these are date-only columns, and UTC
 * midnight reads back as the previous day west of UTC — which here would put
 * a tenant on the wrong day of their own tenancy.
 */
export function stayProgress(
  booking: {
    move_in_date?: unknown;
    start_date?: unknown;
    actual_move_out_date?: unknown;
    move_out_date?: unknown;
    end_date?: unknown;
  } | null,
  now: number = Date.now()
): StayProgress {
  const moveInRaw = String(booking?.move_in_date ?? booking?.start_date ?? "");
  if (!moveInRaw) return { kind: "unknown", percent: null };
  const moveIn = parseDateOnly(moveInRaw);
  if (Number.isNaN(moveIn.getTime())) return { kind: "unknown", percent: null };

  const today = startOfDay(now);
  const start = startOfDay(moveIn.getTime());

  if (today < start) {
    const days = Math.round((start - today) / DAY_MS);
    return days <= MOVE_IN_COUNTDOWN_DAYS
      ? { kind: "days_until_move_in", days, percent: null }
      : { kind: "before_move_in", moveInIso: moveInRaw, percent: null };
  }

  /*
    The END of the stay is the CONFIRMED move-out where there is one.

    `end_date` is only the earliest possible move-out — contracts roll over —
    so a tenant past their minimum term would otherwise show a progress bar
    pinned at 100% and "0 days left" for the rest of their tenancy. Same rule
    rent-materialize uses.
  */
  const endRaw = String(
    booking?.actual_move_out_date ?? booking?.move_out_date ?? ""
  );
  if (!endRaw) return { kind: "unknown", percent: null };
  const end = parseDateOnly(endRaw);
  if (Number.isNaN(end.getTime())) return { kind: "unknown", percent: null };

  const endDay = startOfDay(end.getTime());
  const total = Math.max(1, Math.round((endDay - start) / DAY_MS));
  const elapsed = Math.round((today - start) / DAY_MS);
  const daysLeft = Math.max(0, total - elapsed);
  const percent = Math.min(
    100,
    Math.max(0, Math.round((elapsed / total) * 100))
  );

  return daysLeft <= MOVE_OUT_COUNTDOWN_DAYS
    ? { kind: "ending", daysLeft, percent }
    : { kind: "living", day: elapsed + 1, total, percent };
}
